import { guestSessionOnMessage } from "../actions/onMessageActions/guestSessions";
import { noneSessionOnMessage } from "../actions/onMessageActions/noneSessions";
import { notarySessionOnMessage } from "../actions/onMessageActions/notarySessions";
import { sessionsOnMessage } from "../actions/onMessageActions/sessions";
import { userSessionOnMessage } from "../actions/onMessageActions/userSessions";
import { subUserSessionOnMessage } from "../actions/onMessageActions/subUserSessions";
import { updateSessionWsConnection } from "../actions/sessionsActions";
import { privateProviderSessionOnMessage } from "../actions/onMessageActions/privateProviderSessions";

// Registry to dedupe sockets per sessionType across middleware instances
const socketRegistry = new Map(); // sessionType -> WebSocket | null
const getSocket = (sessionType) => socketRegistry.get(sessionType) || null;
const setSocket = (sessionType, socket) =>
  socketRegistry.set(sessionType, socket);

const onMessageActions = {
  guestSession: guestSessionOnMessage,
  notarySession: notarySessionOnMessage,
  userSession: userSessionOnMessage,
  privateProviderSession: privateProviderSessionOnMessage,
  subUserSession: subUserSessionOnMessage,
  sessions: sessionsOnMessage,
  noneSession: noneSessionOnMessage,
};

const createWebSocketMiddleware = (sessionType) => {
  // use registry instead of local var to avoid duplicate connections
  let reconnectTimer = null;
  let reconnectAttempts = 0;
  const maxReconnectAttempts = 6;
  let connectionTimeout = 15000; // 10 seconds timeout for connection
  const reconnectDelay = 2000; // 5 seconds delay for reconnection
  let connectionTimer = null;

  const getSessionToken = (state, sessionType) => {
    switch (sessionType) {
      case "userSession":
        return state.sessions?.userSession.token;
      case "notarySession":
        return state.sessions?.notarySession.token;
      case "guestSession":
        return state.sessions?.guestSession.token;
      case "subUserSession":
        return state.sessions?.subUserSession.token;
      case "privateProviderSession":
        return state.sessions?.privateProviderSession.token;
      default:
        return null; // or appropriate error handling
    }
  };

  const handleWebSocketMessage = async (dispatch, getState, receivedData) => {
    const sessionToken = getSessionToken(getState(), sessionType);
    const actionCreator = onMessageActions[receivedData.type];

    if (actionCreator) {
      if (
        receivedData.type === sessionType ||
        receivedData.type === "sessions" ||
        receivedData.type === "noneSession"
      ) {
        if (
          receivedData?.event === "heartbeat" &&
          receivedData.type === sessionType
        ) {
          if (receivedData.token === sessionToken) {
            const exp = sessionStorage.getItem("exp");
            const extended = sessionStorage.getItem("extended");

            if (receivedData.data.will_expire_soon && !extended) {
              sessionStorage.setItem("willExpire", "true");
              const event = new Event("expireUpdate");
              window.dispatchEvent(event);
            } else if (!receivedData.data.will_expire_soon) {
              sessionStorage.removeItem("willExpire");
              sessionStorage.removeItem("extended");
            }

            if (receivedData.data.session_expired && !extended) {
              sessionStorage.setItem("expiredSession", "true");
              sessionStorage.clear(); // Clear session storage on expiration

              await dispatch({
                type: `${sessionType.toUpperCase()}_WEBSOCKET_DISCONNECT`,
              });
              window.location.href = "/login";
              return;
            }

            // Echo back the heartbeat message
            const response = JSON.stringify({
              type: receivedData.type, // Echo back the received type
              token: receivedData.token, // Echo back the token for verification
              extended: extended,
              exp: exp,
              timestamp: new Date().toISOString(),
            });

            try {
              const socket = getSocket(sessionType);
              if (socket && socket.readyState === WebSocket.OPEN) {
                socket.send(response);
              }
            } catch (e) {
              console.error("Failed to send heartbeat response:", e);
            }
          } else {
            console.error(
              `Received token does not match stored token. Received: ${receivedData.token}, Expected: ${sessionToken}`
            );
          }
        } else {
          await dispatch(actionCreator(receivedData));
        }
      } else {
        // Handle unexpected session types if needed
      }
    } else {
      console.error("No action creator found for type:", receivedData.type);
    }
  };

  const connectWebSocket = (dispatch, getState, url, token) => {
    const sessions = getState().sessions;
    const { sessionForcedLogout } = sessions;

    // Prevent duplicate connections under StrictMode double-dispatch
    const existing = getSocket(sessionType);
    if (
      existing &&
      (existing.readyState === WebSocket.OPEN ||
        existing.readyState === WebSocket.CONNECTING)
    ) {
      console.warn(
        "WebSocket already connecting/open; skipping duplicate connect."
      );
      return;
    }
    if (existing !== null && existing.readyState !== WebSocket.CLOSED) {
      try {
        existing.close();
      } catch {}
    }
    setSocket(sessionType, new WebSocket(url));
    const socket = getSocket(sessionType);

    connectionTimer = setTimeout(() => {
      if (socket && socket.readyState === WebSocket.CONNECTING) {
        console.error(
          "WebSocket connection timed out. Attempting to reconnect..."
        );
        socket.close();
        reconnectWebSocket(dispatch, getState, url, token);
      }
    }, connectionTimeout);

    socket.onopen = () => {
      clearInterval(reconnectTimer);
      clearTimeout(connectionTimer);
      reconnectAttempts = 0;

      const state = getState();
      const sessions = state?.sessions;
      const { currentSession } = sessions; // e.g., "userSession"

      const currentConnection = sessions?.[currentSession];

      if (
        currentConnection?.isValidated &&
        currentConnection?.token &&
        currentConnection?.wsConnected &&
        currentConnection?.token
      ) {
        return;
      } else {
        dispatch(
          updateSessionWsConnection({
            sessionType: sessionType,
            wsConnected: true,
          })
        );
      }
    };

    socket.onmessage = (event) => {
      const receivedData = JSON.parse(event.data);
      handleWebSocketMessage(dispatch, getState, receivedData);
    };

    socket.onerror = (error) => {
      if (!sessionForcedLogout) {
        console.error(`${sessionType} WebSocket error:`, error);
      }
    };

    socket.onclose = (event) => {
      clearTimeout(connectionTimer);
      setSocket(sessionType, null);
      if (reconnectAttempts < maxReconnectAttempts) {
        setTimeout(() => {
          if (!sessionForcedLogout) {
            reconnectWebSocket(dispatch, getState, url, token);
          }
        }, 2000); // Add a small delay
      } else {
        window.location.href = "/login";
      }
    };
  };

  const reconnectWebSocket = (dispatch, getState, url, token) => {
    const sessions = getState().sessions;
    const { sessionForcedLogout } = sessions;

    if (sessionForcedLogout) {
      return;
    }

    if (reconnectAttempts === 2) {
      window.location.reload();
    }

    reconnectAttempts++;
    if (reconnectAttempts === 0) {
      // Immediate retry on the first disconnect
      connectWebSocket(dispatch, getState, url, token);
    } else if (reconnectAttempts <= maxReconnectAttempts) {
      const delay = Math.min(reconnectDelay * 1.5 ** reconnectAttempts, 10000); // Faster reconnect
      reconnectTimer = setTimeout(
        () => connectWebSocket(dispatch, getState, url, token),
        delay
      );
    } else {
      window.location.href = "/login";
    }
  };

  return (store) => (next) => (action) => {
    const { dispatch, getState } = store;
    // Extract the session type from the action type
    const actionPrefix = action?.type?.split("_")[0].toLowerCase();

    // Only handle actions that match this middleware's sessionType
    if (actionPrefix !== sessionType.toLowerCase()) {
      return next(action);
    }

    // Process only the actions for this sessionType
    switch (action.type) {
      case `${sessionType.toUpperCase()}_WEBSOCKET_CONNECT`:
        connectWebSocket(
          dispatch,
          getState,
          action.payload.url,
          action.payload.token
        );
        break;
      case `${sessionType.toUpperCase()}_WEBSOCKET_DISCONNECT`:
        {
          const socket = getSocket(sessionType);
          if (socket !== null) {
            try {
              socket.onclose = null;
              socket.close();
            } catch {}
            setSocket(sessionType, null);
          }
        }
        dispatch(
          updateSessionWsConnection({
            sessionType: sessionType,
            wsConnected: false,
          })
        );
        clearInterval(reconnectTimer);
        clearTimeout(connectionTimer);
        reconnectAttempts = 0;
        break;
      case `${sessionType.toUpperCase()}_WEBSOCKET_SEND_MESSAGE`:
        {
          const socket = getSocket(sessionType);
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(action.payload.message));
          }
        }
        break;
      default:
        return next(action);
    }
  };
};

export default createWebSocketMiddleware;
